import React, { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useTheme } from "../Navbar/Theme";
import "./../styles/Skills.css";

// ── 1. Import Sound Controller & Card Audio Asset Only ──
import { sounds } from "../Utils/soundEffects";
import skillCardHoverSfx from "../../assets/sounds/skill-hover.mp3";

gsap.registerPlugin(ScrollTrigger);

// ── Explicit Theme-to-Pill Color Mapping ──
const SKILL_THEME_SETS = {
  0: {
    uiux: "#887f84",
    graphic: "#9b75b7",
    motion: "#c8c9cd",
    frontend: "#727ca7",
  },
  1: {
    uiux: "#de68a7",
    graphic: "#df7c65",
    motion: "#99ab6c",
    frontend: "#b89885",
  },
  2: {
    uiux: "#a7bcde",
    graphic: "#c5c5c5",
    motion: "#d79db6",
    frontend: "#8687c5",
  },
  3: {
    uiux: "#d388a6",
    graphic: "#a98b7a",
    motion: "#c38376",
    frontend: "#c1b6aa",
  },
};

const SKILLS_LIST = [
  {
    id: "uiux",
    title: "UI/UX Design",
    key: "uiux",
    rot: -0.6,
    yOffset: 0,
    zIndex: 4,
    tags: [
      "Figma",
      "Wireframing",
      "UX Research",
      "Interaction Design",
      "Miro",
      "Prototyping",
      "AutoLayout",
      "FigJam",
      "Responsive UI",
    ],
  },
  {
    id: "graphic",
    title: "Graphic Design",
    key: "graphic",
    rot: 2.3,
    yOffset: -12,
    zIndex: 3,
    tags: [
      "Adobe Illustrator",
      "Adobe Photoshop",
      "GIMP",
      "Canva",
      "Brand Identity",
      "Krita",
    ],
  },
  {
    id: "motion",
    title: "Motion Design",
    key: "motion",
    rot: -1.4,
    yOffset: 3,
    zIndex: 2,
    tags: [
      "DaVinci Resolve",
      "Jitter",
      "Motion Graphics",
      "Micro Interactions",
      "Animated UI",
      "Video Editing",
      "Linearity Move",
    ],
  },
  {
    id: "frontend",
    title: "Frontend Tools",
    key: "frontend",
    rot: 0.9,
    yOffset: -3,
    zIndex: 1,
    tags: [
      "ReactJS",
      "NextJS",
      "HTML5",
      "CSS3",
      "TailwindCSS",
      "Git",
      "GitHub",
      "Notion",
      "Data Analysis",
    ],
  },
];

export default function Skills({ themeIndex }) {
  const containerRef = useRef(null);
  const [activeCardId, setActiveCardId] = useState(null);

  const { activeTheme } = useTheme();
  const currentThemeIdx = themeIndex !== undefined ? themeIndex : (activeTheme ?? 0);
  const currentColors = SKILL_THEME_SETS[currentThemeIdx] || SKILL_THEME_SETS[0];

  // ── 2. Preload Only Card Audio on Mount ──
  useEffect(() => {
    sounds.loadSound("skillCardHover", skillCardHoverSfx);

    const unlockAudio = () => {
      sounds.init();
      window.removeEventListener("pointerdown", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };

    window.addEventListener("pointerdown", unlockAudio);
    window.addEventListener("keydown", unlockAudio);

    return () => {
      window.removeEventListener("pointerdown", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };
  }, []);

  // Mobile outside click listener (< 860px)
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (window.innerWidth <= 860) {
        if (!e.target.closest(".skill-card-bar")) {
          setActiveCardId(null);
        }
      }
    };

    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, []);

  const handleCardClick = (id) => {
    if (window.innerWidth <= 860) {
      sounds.play("skillCardHover", 0.25);
      setActiveCardId((prev) => (prev === id ? null : id));
    }
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 75%",
          end: "bottom 20%",
          toggleActions: "restart pause resume reverse",
        },
      });

      tl.fromTo(
        ".skills-left-col > *",
        { opacity: 0, y: 35 },
        { opacity: 1, y: 0, duration: 0.7, stagger: 0.1, ease: "power3.out" }
      ).fromTo(
        ".skill-card-slot",
        { opacity: 0, x: 40 },
        {
          opacity: 1,
          x: 0,
          duration: 0.8,
          stagger: 0.12,
          ease: "power2.out",
        },
        "-=0.5"
      );
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <section className="skills-section-wrapper" ref={containerRef}>
      <div className="skills-container-grid">
        {/* ── Left Column: Typography & Content ── */}
        <div className="skills-left-col">
          <div className="skills-heading-lockup">
            <h2 className="skills-main-title">Tool of</h2>
            <h3 className="skills-trade-title">the Trade</h3>
          </div>

          <p className="skills-para-short">
            My favourite weapons for turning blank canvases into things people want to use, click through, and maybe even remember!
          </p>

          <p className="skills-para-long">
            I’m always trying out something new, a different design tool, a new animation workflow, or some random thing I stumbled across at 2 a.m. Some make it into my regular toolkit, some don’t, but experimenting is half the fun.
          </p>
        </div>

        {/* ── Right Column: Interactive Staggered Glass Cards ── */}
        <div className="skills-right-col">
          {SKILLS_LIST.map((skill) => {
            const isOpen = activeCardId === skill.id;
            const orbColor = currentColors[skill.key];

            return (
              <div className="skill-card-slot" key={skill.id}>
                <div
                  className={`skill-card-bar ${isOpen ? "open-active" : ""}`}
                  style={{
                    "--card-rot": `${skill.rot}deg`,
                    "--card-y": `${skill.yOffset}px`,
                    "--card-z": skill.zIndex,
                  }}
                  onMouseEnter={() => sounds.play("skillCardHover", 0.25)}
                  onClick={() => handleCardClick(skill.id)}
                >
                  <div className="skill-bar-header">
                    <span className="skill-bar-title">{skill.title}</span>

                    {/* 3D Glass Stone Orb */}
                    <div
                      className="skill-orb"
                      style={{ "--orb-base-color": orbColor }}
                    >
                      <div className="orb-teardrop-shine top-left" />
                      <div className="orb-teardrop-shine bottom-right" />
                      <svg
                        className="orb-arrow-icon"
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="5" y1="19" x2="19" y2="5" />
                        <polyline points="7 5 19 5 19 17" />
                      </svg>
                    </div>
                  </div>

                  {/* Software Tag Pills Drawer */}
                  <div className="skill-tags-drawer">
                    <div className="skill-tags-inner">
                      {skill.tags.map((tag, idx) => (
                        <span className="skill-white-pill" key={idx}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}